import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import { useHistory } from 'react-router-dom';

import { getS3StorageAuditSummary, S3StorageAuditSummary } from '../../../api/s3StorageAuditApi';
import { ORGANIZATION } from '../../../enums/TYPES';
import { PATHS } from '../../../enums/PATHS';
import { formatStorageBytes, formatStorageDate, formatStorageLabel } from './utils';
import styles from './S3StorageDetails.module.scss';

type StorageSummaryTooltipProps = {
  organization: ORGANIZATION;
  children: React.ReactElement;
};

const StorageSummaryTooltip: React.FC<StorageSummaryTooltipProps> = ({ organization, children }) => {
  const history = useHistory();
  const [summary, setSummary] = useState<S3StorageAuditSummary | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const loadSummary = async () => {
    if (loaded || loading) return;
    setLoading(true);
    setFailed(false);
    try {
      const response = await getS3StorageAuditSummary(String(organization.id));
      setSummary(response.data);
      setLoaded(true);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const openDetails = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    history.push(PATHS.SA_ORGANIZATION_STORAGE.path.replace(':organizationId', String(organization.id)));
  };

  const displayedBytes = summary?.displayed_storage_bytes ?? Number(organization.storage_usage || 0) * 1_000_000_000;
  const status = summary?.status || 'NOT_SCANNED';

  const tooltip = (
    <div className={styles.storageTooltip} onClick={(event) => event.stopPropagation()}>
      <div className={styles.tooltipHeader}>
        <span>S3 storage</span>
        {!loading && <span className={styles.statusBadge}>{formatStorageLabel(status)}</span>}
      </div>

      {loading ? (
        <div className={styles.tooltipMessage}>Loading storage data…</div>
      ) : (
        <>
          <div className={styles.tooltipMetric}>
            <span>Displayed storage usage</span>
            <strong>{formatStorageBytes(displayedBytes)}</strong>
          </div>
          <div className={styles.tooltipMetric}>
            <span>Actual S3 (scanned paths)</span>
            <strong>{formatStorageBytes(summary?.actual_storage_bytes)}</strong>
          </div>
          <div className={styles.tooltipMetric}>
            <span>S3 minus displayed</span>
            <strong>
              {formatStorageBytes(summary?.difference_bytes)}
              {summary?.difference_percent !== null && summary?.difference_percent !== undefined
                ? ` (${summary.difference_percent.toFixed(1)}%)`
                : ''}
            </strong>
          </div>
          <div className={styles.tooltipMetric}>
            <span>Suspected orphans</span>
            <strong>
              {summary ? `${summary.suspected_orphans_count} · ${formatStorageBytes(summary.suspected_orphans_bytes)}` : '—'}
            </strong>
          </div>
          <div className={styles.tooltipMetric}>
            <span>Last scanned</span>
            <strong>{formatStorageDate(summary?.last_scanned_at)}</strong>
          </div>
          {(failed || !summary?.last_scanned_at) && (
            <div className={styles.tooltipMessage}>
              {failed ? 'Storage data is currently unavailable.' : 'S3 data not scanned yet.'}
            </div>
          )}
          {summary?.scan_error && <div className={styles.tooltipError}>{summary.scan_error}</div>}
        </>
      )}

      <button type="button" className={styles.tooltipLink} onClick={openDetails}>
        View details
      </button>
    </div>
  );

  if (organization.deletion_status === 'ARCHIVED') return children;

  return (
    <Tooltip
      arrow
      enterDelay={350}
      onOpen={loadSummary}
      placement="top"
      title={tooltip}
      componentsProps={{ tooltip: { sx: { bgcolor: 'transparent', maxWidth: 'none', p: 0 } } }}
    >
      {children}
    </Tooltip>
  );
};

export default StorageSummaryTooltip;
